import { Group } from '../../../api/groups';
import StatusPills from './StatusPills';
import { formatDate } from '../../../utils/formatDate';
import { useCallback, useContext, useEffect, useState } from 'react';
import { fetchLinkedStudentModules, GroupModuleYearUnwind } from '../../../api/my-groups';
import { useTranslation } from 'react-i18next';
import ModalContext from '../../../contexts/ModalContext';
import EditGroupModal from '../modals/code-based/edit-group/EditGroupModal';
import useGroups from '../hooks/useGroups';
import { useSchoolYear } from '@pelckmans/business-components/context/SchoolYearContext';

import CalendarIcon from '@pelckmans/elementis/icons/calendar';
import List from '@pelckmans/elementis/components/list';
import Pill from '@pelckmans/elementis/components/pill';
import PillGroup from '@pelckmans/elementis/components/pill-group';
import useStudioGroup from '../../../hooks/useStudioGroup';

export const Status = ({ group }: { group: Group }) => {
  const { t } = useTranslation();
  if (group.status === 'closed') {
    return <Pill prominence="danger">{t('PAGES.MY_GROUPS.LIST_GROUP.STATE.CLOSED')}</Pill>;
  }
  if (group.status === 'unpublished') {
    return <Pill prominence="warning">{t('PAGES.MY_GROUPS.LIST_GROUP.STATE.UNPUBLISHED')}</Pill>;
  }
  if (group.status === 'published') {
    return (
      <PillGroup role="group" aria-label={undefined}>
        <Pill key="published" prominence="success">
          {t('PAGES.MY_GROUPS.LIST_GROUP.STATE.PUBLISHED')}
        </Pill>
        {group.startDate && group.endDate && (
          <Pill key="date-range" prominence="neutral" icon={<CalendarIcon />}>
            {t('PAGES.MY_GROUPS.LIST_GROUP.STATE.DATE_RANGE', {
              start: formatDate(group.startDate),
              end: formatDate(group.endDate),
            })}
          </Pill>
        )}
      </PillGroup>
    );
  }
};

export default function GroupListItem({ group }: { group: Group }): JSX.Element | null {
  const { t } = useTranslation();
  const [linkedModules, setLinkedModules] = useState<GroupModuleYearUnwind[]>([]);
  const { showModal } = useContext(ModalContext);
  const { selectedSchoolYear, onGroupsChange } = useGroups();
  const { nextSchoolYear } = useSchoolYear();
  const studioGroup = useStudioGroup();

  useEffect(() => {
    async function fetchLinkedModules() {
      const modules = await fetchLinkedStudentModules(group.id);
      setLinkedModules(modules);
    }
    fetchLinkedModules();
  }, [group.id]);

  const handleGroupUpdated = useCallback(
    (updatedGroup: Group, updatedLinkedModules: GroupModuleYearUnwind[]) => {
      onGroupsChange(prev => prev?.map(g => (g.id === updatedGroup.id ? updatedGroup : g)));
      setLinkedModules(updatedLinkedModules);
    },
    [onGroupsChange],
  );

  const handleGroupDeleted = useCallback(() => {
    onGroupsChange(prev => prev?.filter(g => g.id !== group.id));
  }, [group.id, onGroupsChange]);

  const handleMembersChange = useCallback(
    (total: number) => {
      onGroupsChange(prev => prev?.map(g => (g.id === group.id ? { ...g, totalAccepted: total } : g)));
    },
    [group.id, onGroupsChange],
  );

  if (!selectedSchoolYear || !nextSchoolYear) return null;

  return (
    <List.Item
      title={group.name}
      subText={
        group.status === 'published' ? t('PAGES.MY_GROUPS.LIST_GROUP.COURSE_CODE', { code: group.code }) : undefined
      }
      onClick={() =>
        showModal(
          <EditGroupModal
            id="edit-code-based-group-modal"
            selectedSchoolYear={selectedSchoolYear}
            group={group}
            onGroupUpdated={handleGroupUpdated}
            onGroupDeleted={handleGroupDeleted}
            onMembersChange={handleMembersChange}
            onLinkedModulesChange={setLinkedModules}
            nextSchoolYear={nextSchoolYear}
          />,
        )
      }
      as="button"
      contentArea={<StatusPills group={group} linkedModules={linkedModules} />}
      pillGroupProps={
        studioGroup === 'VO'
          ? {
              role: 'group',
              'aria-label': t('PAGES.MY_GROUPS.LIST_GROUP.STATE.A11Y_LABEL'),
              children: <Status group={group} />,
            }
          : undefined
      }
    />
  );
}
